import { useCallback } from 'react';
import { useToast } from './useToast.js';

const GENERIC_ERROR_MESSAGE = 'Não foi possível concluir a solicitação. Tente novamente.';
const CONFLICT_ERROR_MESSAGE = 'Já existe um cadastro com os dados informados.';

const TECHNICAL_MESSAGES = new Set([
  'Unexpected error.',
  'Unexpected error',
  'Internal server error',
  'Network request failed',
]);

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const getGraphQlErrors = (error) => [
  ...(error?.graphQLErrors || []),
  ...(error?.networkError?.result?.errors || []),
];

/**
 * Normaliza erros tecnicos em mensagens seguras para o usuario.
 *
 * Regra de negocio:
 * - Conflitos de cadastro não devem informar qual dado já existe
 * - Mensagens internas do GraphQL/rede viram fallback generico
 *
 * @param {unknown} error - Erro Apollo, Error ou string recebida do fluxo
 * @param {string} fallback - Mensagem segura quando o erro nao for exibivel
 * @returns {string} Mensagem publica para toast/DOM
 */
export const getUserErrorMessage = (error, fallback = GENERIC_ERROR_MESSAGE) => {
  const graphQlErrors = getGraphQlErrors(error);
  const graphQlCode = graphQlErrors.find((item) => item?.extensions?.code)?.extensions?.code;
  const graphQlMessage = graphQlErrors.find((item) => item?.message)?.message;
  const networkMessage = error?.networkError?.message;
  const rawMessage = typeof error === 'string'
    ? error
    : graphQlMessage || networkMessage || error?.message;
  const normalizedMessage = normalizeText(rawMessage);

  if (graphQlCode === 'CONTACT_CONFLICT' || normalizedMessage.includes('contato ja cadastrado')) {
    return CONFLICT_ERROR_MESSAGE;
  }

  if (!rawMessage || TECHNICAL_MESSAGES.has(rawMessage)) {
    return fallback;
  }

  return rawMessage;
};

/**
 * Exibe erros em toast usando a mesma mensagem segura renderizada no DOM.
 *
 * @returns {{showError: Function, getErrorMessage: Function}} Helpers de erro
 */
export const useError = () => {
  const { error: showErrorToast } = useToast();

  const showError = useCallback((error, options = {}) => {
    const { fallback, ...toastOptions } = options;
    const message = getUserErrorMessage(error, fallback);
    showErrorToast(message, toastOptions);
    return message;
  }, [showErrorToast]);

  return {
    showError,
    getErrorMessage: getUserErrorMessage,
  };
};
